import { useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Card, Input, Select, Modal, Table, StatusPill, EmptyState, Skeleton, useToast } from '../../../design-system';

const TYPES = ['Trade_License', 'FSSAI', 'Shops_Estab', 'Drug', 'Factory', 'LUT', '12A', '80G', 'DSC'];
const TYPE_LABELS = {
  Trade_License: 'Trade License', FSSAI: 'FSSAI', Shops_Estab: 'Shops & Estab.', Drug: 'Drug License',
  Factory: 'Factory License', LUT: 'LUT', '12A': '12A', '80G': '80G', DSC: 'DSC',
};
const STATUSES = ['active', 'expired', 'renewed', 'cancelled'];

const dateOnly = (v) => (v ? v.slice(0, 10) : '');
const EMPTY = () => ({
  type: 'Trade_License', ownerType: 'Client', ownerId: '', number: '', issuingAuthority: '',
  validFrom: '', validTo: '', status: 'active', dscClass: '', usageType: '', notes: '',
});

// Licenses, LUT, 12A/80G certificates, and DSCs — the one generic expirables
// engine. Its own collection (GET/POST /api/expirables), not embedded on the
// client. DSCs attach to a signatory (ownerType = Signatory).
function ExpirablesManager({ clientId, signatories }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field, v) => setForm((f) => ({ ...f, [field]: v }));

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/api/expirables', { params: { clientId } });
      setItems(res.data);
    } catch (err) {
      // non-fatal
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const openAdd = () => { setEditing(null); setForm(EMPTY()); setError(''); setModalOpen(true); };
  const openEdit = (x) => {
    setEditing(x._id);
    setForm({
      type: x.type, ownerType: x.ownerType || 'Client', ownerId: x.ownerType === 'Signatory' ? x.ownerId : '',
      number: x.number || '', issuingAuthority: x.issuingAuthority || '',
      validFrom: dateOnly(x.validFrom), validTo: dateOnly(x.validTo), status: x.status || 'active',
      dscClass: x.dscClass || '', usageType: x.usageType || '', notes: x.notes || '',
    });
    setError('');
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.validTo) { setError('Valid-to date is required'); return; }
    if (form.ownerType === 'Signatory' && !form.ownerId) { setError('Pick the signatory this DSC belongs to'); return; }
    setSaving(true);
    setError('');
    const payload = { ...form, clientId };
    if (!payload.validFrom) delete payload.validFrom;
    if (payload.ownerType !== 'Signatory') delete payload.ownerId;
    try {
      if (editing) {
        await apiClient.put(`/api/expirables/${editing}`, payload);
      } else {
        await apiClient.post('/api/expirables', payload);
      }
      setModalOpen(false);
      toast.success(editing ? 'Updated' : 'Added');
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await apiClient.delete(`/api/expirables/${id}`);
      fetchItems();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to delete');
    }
  };

  const isDsc = form.type === 'DSC';
  // A DSC must belong to a person; force ownerType accordingly for clarity.
  const ownerTypeForType = isDsc ? 'Signatory' : form.ownerType;

  const signatoryName = (id) => signatories.find((s) => s._id === id)?.name || 'Signatory';
  const expiringSoon = (x) => x.status === 'active' && new Date(x.validTo) < new Date(Date.now() + 30 * 864e5);
  const expired = (x) => x.status === 'active' && new Date(x.validTo) < new Date();

  const columns = [
    { key: 'type', header: 'Type', render: (x) => TYPE_LABELS[x.type] || x.type },
    { key: 'owner', header: 'Owner', render: (x) => (x.ownerType === 'Signatory' ? signatoryName(x.ownerId) : 'Client') },
    { key: 'number', header: 'Number', render: (x) => x.number || '-' },
    { key: 'validTo', header: 'Valid To', render: (x) => dateOnly(x.validTo) },
    {
      key: 'status', header: 'Status',
      render: (x) => <StatusPill status={expired(x) ? 'inactive' : x.status === 'active' ? 'active' : 'inactive'} label={expired(x) ? 'Expired' : x.status} />,
    },
    {
      key: 'actions', header: '', render: (x) => (
        <div className="enrollment-actions">
          <Button variant="secondary" size="sm" onClick={() => openEdit(x)}>Edit</Button>
          <button className="btn-remove" onClick={() => remove(x._id)}>Delete</button>
        </div>
      ),
    },
  ];

  return (
    <div className="tab-content">
      <Card>
        <Card.Header
          title="Licenses, Certificates & DSCs"
          action={<Button size="sm" onClick={openAdd}>+ Add Expirable</Button>}
        />
        {loading ? (
          <Skeleton.Text lines={3} />
        ) : items.length === 0 ? (
          <EmptyState icon="⏳" title="Nothing tracked yet" description="Add trade licenses, LUT, 12A/80G, or DSCs to get renewal reminders." />
        ) : (
          <Table columns={columns} data={items} rowClassName={(x) => (expiringSoon(x) ? 'ds-table-row-overdue' : '')} />
        )}
      </Card>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Expirable' : 'Add Expirable'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={saving}>{editing ? 'Save' : 'Add'}</Button>
          </>
        }
      >
        <form className="client-form" onSubmit={save}>
          {error && <div className="form-error" role="alert">{error}</div>}
          <div className="form-row">
            <Select label="Type" value={form.type} onChange={(e) => set('type', e.target.value)}>
              {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </Select>
            <Select
              label="Owner"
              value={ownerTypeForType}
              onChange={(e) => set('ownerType', e.target.value)}
              disabled={isDsc}
            >
              <option value="Client">Client</option>
              <option value="Signatory">Signatory (person)</option>
            </Select>
            {ownerTypeForType === 'Signatory' && (
              <Select label="Signatory" value={form.ownerId} onChange={(e) => set('ownerId', e.target.value)}>
                <option value="">-- Select --</option>
                {signatories.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
              </Select>
            )}
          </div>
          <div className="form-row">
            <Input label="Number" value={form.number} onChange={(e) => set('number', e.target.value)} />
            <Input label="Issuing Authority" value={form.issuingAuthority} onChange={(e) => set('issuingAuthority', e.target.value)} />
          </div>
          <div className="form-row">
            <Input label="Valid From" type="date" value={form.validFrom} onChange={(e) => set('validFrom', e.target.value)} />
            <Input label="Valid To *" type="date" value={form.validTo} onChange={(e) => set('validTo', e.target.value)} />
            <Select label="Status" value={form.status} onChange={(e) => set('status', e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </div>
          {isDsc && (
            <div className="form-row">
              <Input label="DSC Class" value={form.dscClass} onChange={(e) => set('dscClass', e.target.value)} placeholder="Class 3" />
              <Input label="Usage Type" value={form.usageType} onChange={(e) => set('usageType', e.target.value)} placeholder="Signing / DGFT" />
            </div>
          )}
          <Input label="Notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </form>
      </Modal>
    </div>
  );
}

export default ExpirablesManager;
